import type { Attributes } from "@opentelemetry/api";
import { useEffect, useMemo, useRef } from "react";

import { Events, trackBeacon } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    clippingOutlinesAreGpuComputed,
    clippingTrianglesAreDoubleSided,
} from "features/clippingPlanes/clippingMobilePolicy";
import { selectClippingPlanes, selectDeviceProfile } from "features/render";
import { Platform, selectIsProcoreAndroid, selectIsProcoreDesign, selectIsProcoreIOS } from "slices/explorer";
import { telemetry } from "telemetry";
import { ackControlledUnload, consumeUncleanCrashSession, writeDirtyCrashSession } from "telemetry/crashSessionMarker";

/** Keep a recent memory snapshot for the crash beacon without high-volume telemetry. */
export const MEMORY_SNAPSHOT_INTERVAL_MS = 30_000;

/**
 * If the view never appears (init failed after GPU detection), still report the
 * previous session rather than dropping the iOS jetsam signal.
 */
export const ABNORMAL_EXIT_WAIT_FOR_DEVICE_MS = 10_000;

function deviceGlobalsAreReady(): boolean {
    const attrs = telemetry.getGlobalAttributes?.();
    return typeof attrs?.["device.gpu_tier"] === "number";
}

/**
 * Honeycomb board: https://ui.honeycomb.io/procore-eb/environments/production/board/jJCVVLhRZVk/BIM-Viewer-Mobile
 *
 * Suggested queries (same dataset as `viewer.load`; `bim.crash.kind` is the join key):
 * - COUNT WHERE EXISTS(bim.crash.kind) GROUP BY bim.crash.kind, platform, device.gpu_tier
 * - COUNT WHERE EXISTS(bim.crash.kind) GROUP BY bim.crash.kind, company.id, project.id, scene.id
 * - RATE: COUNT(bim.crash.kind exists) / COUNT(name = viewer.load) broken by platform
 *
 * `webgl_context_lost` also goes to Amplitude. `page_frozen` and `abnormal_exit` are
 * Honeycomb-only until those kinds are proven not to be dominated by force-quit / app-switcher noise.
 */

type RenderStatistics = {
    bufferBytes?: number;
    primitives?: number;
    textureBytes?: number;
};

type ViewWithStatistics = {
    statistics?: { render?: RenderStatistics };
};

/**
 * The configuration half of a snapshot, read from Redux rather than off `view`.
 *
 * `useHandleClipping` runs in a later sibling, so on the update that adds a
 * plane the view still carries the previous clipping flags. Sampling those
 * would freeze "sectioning off" into the heartbeat until the next 30 s tick,
 * which is the window an iOS jetsam lands in. The view keeps the measurement
 * half (`statistics`), which nothing else knows. Render budgets come from the
 * configured device profile — they are not rewritten when a plane is added.
 */
type ClippingHeartbeat = {
    enabled: boolean;
    planeCount: number;
    gpuComputed: boolean;
    trianglesDoubleSided: boolean;
    detailBias: number;
    maxPrimitives: number;
    maxGPUBytes: number | undefined;
};

function addFiniteAttribute(attributes: Attributes, key: string, value: unknown): void {
    if (typeof value === "number" && Number.isFinite(value)) {
        attributes[key] = value;
    }
}

function getMemorySnapshot(view: ViewWithStatistics | undefined, clippingHeartbeat: ClippingHeartbeat): Attributes {
    const attributes: Attributes = {};
    const memory = (
        globalThis.performance as Performance & {
            memory?: { jsHeapSizeLimit?: number; usedJSHeapSize?: number };
        }
    ).memory;
    const render = view?.statistics?.render;

    addFiniteAttribute(attributes, "memory.js_heap_used_bytes", memory?.usedJSHeapSize);
    addFiniteAttribute(attributes, "memory.js_heap_limit_bytes", memory?.jsHeapSizeLimit);
    addFiniteAttribute(attributes, "memory.gpu_buffer_bytes", render?.bufferBytes);
    addFiniteAttribute(attributes, "memory.gpu_texture_bytes", render?.textureBytes);
    addFiniteAttribute(attributes, "render.primitives", render?.primitives);

    addFiniteAttribute(attributes, "render.detail_bias", clippingHeartbeat.detailBias);
    addFiniteAttribute(attributes, "render.max_primitives", clippingHeartbeat.maxPrimitives);
    addFiniteAttribute(attributes, "render.max_gpu_bytes", clippingHeartbeat.maxGPUBytes);

    attributes["clipping.enabled"] = clippingHeartbeat.enabled;
    attributes["clipping.plane_count"] = clippingHeartbeat.planeCount;
    attributes["clipping.gpu_computed"] = clippingHeartbeat.gpuComputed;
    attributes["clipping.triangles_double_sided"] = clippingHeartbeat.trianglesDoubleSided;

    return attributes;
}

function persistDirtySession(memory: Attributes): void {
    writeDirtyCrashSession(memory, telemetry.getGlobalAttributes?.());
}

/**
 * Crash telemetry for the 3D viewer.
 *
 * In-process: WebGL context loss (strongest precursor on low-end devices) and Chromium
 * Page Lifecycle `freeze` (Android TWA). iOS jetsam often fires neither, so a dirty
 * session marker in localStorage lets the next launch report `abnormal_exit`.
 *
 * Honeycomb, via {@link telemetry.beaconCrashSignal}, is the engineering view for every
 * kind (`bim.crash.kind`). Amplitude {@link trackBeacon} stays on context-loss only.
 *
 * Both Honeycomb paths use `sendBeacon`: the page frequently dies right after the signal
 * and anything buffered would never flush. Fires on all platforms.
 */
export function useCrashTelemetry() {
    const {
        state: { canvas, view },
    } = useExplorerGlobals();
    const deviceProfile = useAppSelector(selectDeviceProfile);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const isProcoreAndroid = useAppSelector(selectIsProcoreAndroid);
    const isProcoreIOS = useAppSelector(selectIsProcoreIOS);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const latestMemorySnapshot = useRef<Attributes>({});
    const didConsumePreviousSession = useRef(false);
    const clippingHeartbeat = useMemo<ClippingHeartbeat>(() => {
        const enabled = clippingPlanes?.enabled ?? false;
        const planeCount = clippingPlanes?.planes?.length ?? 0;
        const { isMobile } = deviceProfile;

        return {
            enabled,
            planeCount,
            gpuComputed: clippingOutlinesAreGpuComputed(isMobile, isProcoreDesign),
            trianglesDoubleSided: clippingTrianglesAreDoubleSided(planeCount > 0),
            detailBias: deviceProfile.detailBias,
            maxPrimitives: deviceProfile.limits.maxPrimitives,
            maxGPUBytes: deviceProfile.limits.maxGPUBytes,
        };
    }, [clippingPlanes?.enabled, clippingPlanes?.planes?.length, deviceProfile, isProcoreDesign]);

    useEffect(() => {
        const consumePreviousSession = () => {
            if (didConsumePreviousSession.current) {
                return;
            }

            didConsumePreviousSession.current = true;
            const previous = consumeUncleanCrashSession();
            if (previous) {
                telemetry.beaconCrashSignal("abnormal_exit", {
                    "bim.crash.kind": "abnormal_exit",
                    ...previous,
                });
            }
            // Heartbeat may have skipped while a foreign runtime's record was still stored.
            persistDirtySession(latestMemorySnapshot.current);
        };

        // `view` is created in useHandleInit after `setGlobalAttributes(device.*)`,
        // so waiting for it means scene/company/project and GPU tier are already stamped.
        if (view && deviceGlobalsAreReady()) {
            consumePreviousSession();
            return;
        }

        const timeout = setTimeout(consumePreviousSession, ABNORMAL_EXIT_WAIT_FOR_DEVICE_MS);
        return () => clearTimeout(timeout);
    }, [view]);

    useEffect(() => {
        const sample = () => {
            const snapshot = getMemorySnapshot(view, clippingHeartbeat);
            latestMemorySnapshot.current = snapshot;
            persistDirtySession(snapshot);
        };

        sample();
        const timer = setInterval(sample, MEMORY_SNAPSHOT_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [view, clippingHeartbeat]);

    useEffect(() => {
        const onFreeze = () => {
            telemetry.beaconCrashSignal("page_frozen", {
                "bim.crash.kind": "page_frozen",
                ...latestMemorySnapshot.current,
                ...getMemorySnapshot(view, clippingHeartbeat),
            });
        };
        const onPageHide = () => ackControlledUnload();

        document.addEventListener("freeze", onFreeze);
        window.addEventListener("pagehide", onPageHide);

        return () => {
            document.removeEventListener("freeze", onFreeze);
            window.removeEventListener("pagehide", onPageHide);
        };
    }, [view, clippingHeartbeat]);

    useEffect(() => {
        if (!canvas) {
            return;
        }

        const onContextLost = (event: Event) => {
            // preventDefault keeps the context recoverable, per the WebGL spec.
            event.preventDefault();
            // `bim.crash.kind` duplicates the span name on purpose. iOS and Android
            // report viewer deaths from their own datasets under their own span names,
            // so this shared attribute is what lets one environment-wide Honeycomb
            // query count every way the viewer dies across all three platforms.
            telemetry.beaconCrashSignal("webgl_context_lost", {
                "bim.crash.kind": "webgl_context_lost",
                ...latestMemorySnapshot.current,
                ...getMemorySnapshot(view, clippingHeartbeat),
            });
            trackBeacon(Events.viewer.crashed, {
                reason: "webgl_context_lost",
                platform: isProcoreAndroid ? Platform.Android : isProcoreIOS ? Platform.iOS : Platform.Web,
                gpu_tier: deviceProfile.tier,
                is_mobile: deviceProfile.isMobile,
            });
        };

        canvas.addEventListener("webglcontextlost", onContextLost);

        return () => {
            canvas.removeEventListener("webglcontextlost", onContextLost);
        };
    }, [canvas, clippingHeartbeat, deviceProfile, isProcoreAndroid, isProcoreIOS, view]);
}
